import { defineStore } from 'pinia'
import { range } from '~~/utils/Utils'

export type GuestsState = {
  personen: number
  adults: number
  children: number
  children_ages: number[]
  pets: number
  errorAdultsMaximumExceeded: false | number
}

export type GuestsStore = GuestsState & {
  getChildrenAges: (number | '')[]
  setPersonen: (value: number) => void
  setAdults: (value: number) => void
  setChildren: (value: number) => void
  setAgeOfOneChild: (index: number, value: number) => void
  setPets: (value: number) => void
  setPersonenKeepingChildren: (value: number) => void
  setChildrenKeepingPersonen: (value: number) => void
  readFromParamsWithCheckingMaximum: (params: any, productGuests: any) => void
  getState: () => GuestsState
}

const defaultGuestsState = (): GuestsState => ({
  personen: 2,
  adults: 2,
  children: 0,
  children_ages: [],
  pets: 0,
  errorAdultsMaximumExceeded: false
})

function createGuestsApi (guests: GuestsState) {
  const getChildrenAges = computed(() =>
    range(0, guests.children - 1).map(value => guests.children_ages[value] ?? '')
  )

  function setPersonen (value: number) {
    guests.personen = value
    guests.adults = value
    guests.children = 0
  }

  function setAdults (value: number) {
    guests.adults = value
    guests.personen = value + guests.children
  }

  function setChildren (value: number) {
    guests.children = value
    guests.personen = value + guests.adults
  }

  function setAgeOfOneChild (index: number, value: number) {
    guests.children_ages.splice(index, 1, value)
  }

  function setPets (value: number) {
    guests.pets = value
  }

  function setPersonenKeepingChildren (value: number) {
    guests.personen = value
    guests.adults = value - guests.children
    guests.children = guests.children < value ? guests.children : 0
  }

  function setChildrenKeepingPersonen (value: number) {
    guests.children = value
    guests.adults = guests.personen - value
  }

  function readFromParamsWithCheckingMaximum (params: { personen: number }, productGuests: { max_adults: number }) {
    setPersonen(params.personen)
    if (params.personen <= productGuests.max_adults) {
      return
    }
    setPersonen(productGuests.max_adults)
    guests.errorAdultsMaximumExceeded = productGuests.max_adults
    watch(
      guests,
      () => {
        guests.errorAdultsMaximumExceeded = false
      },
      { once: true, deep: true }
    )
  }

  function getState (): GuestsState {
    return {
      personen: guests.personen,
      adults: guests.adults,
      children: guests.children,
      children_ages: [...guests.children_ages],
      pets: guests.pets,
      errorAdultsMaximumExceeded: guests.errorAdultsMaximumExceeded
    }
  }

  return {
    getChildrenAges,
    setPersonen,
    setAdults,
    setChildren,
    setAgeOfOneChild,
    setPets,
    setPersonenKeepingChildren,
    setChildrenKeepingPersonen,
    readFromParamsWithCheckingMaximum,
    getState
  }
}

function useGuests () {
  const guests = reactive<GuestsState>(defaultGuestsState())
  const api = createGuestsApi(guests)
  return {
    ...toRefs(guests),
    ...api
  }
}

export const useGuestsStore = defineStore('guests', () => useGuests())
export const useGuestsForProductSearchStore = defineStore('guests_for_product_search', () => useGuests())
